import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Icon,
  Text,
} from '@chakra-ui/react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CircleFlag } from 'react-circle-flags'
import {
  fetchVacancyList,
  deleteVacancy,
} from '../../api/recruitment/recruitment'
import { PlusIcon, Trash04Icon } from '../../assets/icons'
import { getCountryNameByCode, phoneNumbers } from '../countries/PhoneDial'
import { useFormMutation } from '../../hooks/useFormMutation'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import FilterButton from './base/FilterButton'
import VacancyTextModal from '../../components/modals/Recruitment/VacancyTextModal'

const VacancyTable: React.FC = () => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const [isVacancyModalOpen, setIsVacancyModalOpen] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const itemsPerPage = 15
  const filtersKey = JSON.stringify(filters)

  const vacancyTexts = useQuery({
    queryKey: ['vacancy-texts', page, searchTerm, filtersKey],
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    queryFn: () =>
      fetchVacancyList({
        search: searchTerm,
        page,
        pageSize: itemsPerPage,
        filters,
      }),
  })

  React.useEffect(() => {
    const currentCount = vacancyTexts.data?.data?.length ?? 0
    if (currentCount < itemsPerPage) return

    const nextPage = page + 1

    queryClient.prefetchQuery({
      queryKey: ['vacancy-texts', nextPage, searchTerm, filtersKey],
      staleTime: 60 * 1000,
      queryFn: () =>
        fetchVacancyList({
          search: searchTerm,
          page: nextPage,
          pageSize: itemsPerPage,
          filters,
        }),
    })
  }, [vacancyTexts.data, page, searchTerm, filters, filtersKey, itemsPerPage, queryClient])

  const deleteMutation = useFormMutation({
    mutationFn: deleteVacancy,
    queryKey: 'vacancy-texts',
    onSuccess: () => setSelectedRows([]),
  })

  const tableData = vacancyTexts?.data?.data || []

  const countryFilterOptions = useMemo(
    () => phoneNumbers.map((c) => ({ id: c.code, name: c.name })),
    [],
  )

  const jobProfileFilterOptions = useMemo(() => {
    const seen = new Map<string, { id: string; name: string }>()
    tableData.forEach((row: RowData) => {
      const profile = (row as RowData & { job_profile?: { id: string; title: string } }).job_profile
      if (profile?.id && profile?.title && !seen.has(String(profile.id))) {
        seen.set(String(profile.id), {
          id: String(profile.id),
          name: profile.title,
        })
      }
    })
    return Array.from(seen.values())
  }, [tableData])

  const updateFilter = (key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  const stripHtml = (html: string) => {
    if (!html) return ''
    return html
      .replace(/<li>/gi, '• ')
      .replace(/<\/li>/gi, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  }

  const columns: Column[] = [
    { label: '', accessor: 'select', type: 'select', width: '5%' },
    {
      label: t('common.fields.title'),
      accessor: 'title',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" fontWeight="600" isTruncated>
          {(row as any).title || 'N/A'}
        </Text>
      ),
    },
    {
      label: t('common.columns.jobProfile'),
      accessor: 'job_profile',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated>
          {(row as any).job_profile?.title || '-'}
        </Text>
      ),
    },
    {
      label: t('common.fields.country'),
      accessor: 'country',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        const countryCode = (row as any).country
        return (
          <Flex alignItems="center" gap="8px">
            {countryCode ? (
              <CircleFlag
                countryCode={countryCode.toLowerCase()}
                style={{ width: '24px', height: '24px' }}
              />
            ) : null}
            <Text textStyle="l-xs" isTruncated>
              {countryCode ? getCountryNameByCode(countryCode) : 'N/A'}
            </Text>
          </Flex>
        )
      },
    },
    {
      label: t('common.columns.text'),
      accessor: 'description',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        const rawText = (row as any).description || ''
        const preview = stripHtml(rawText)
        return (
          <Text textStyle="l-xs" color="solid.700" noOfLines={2} maxW="420px" whiteSpace="normal">
            {preview || 'N/A'}
          </Text>
        )
      },
    },
    { label: '', accessor: 'actions', type: 'actions' },
  ]

  const handleSearch = (term: string) => {
    setPage(1)
    setSearchTerm(term)
  }

  const handleSelectRow = (id: string | number, isChecked: boolean) => {
    const strId = String(id)
    if (isChecked) setSelectedRows((prev) => [...prev, strId])
    else setSelectedRows((prev) => prev.filter((rowId) => rowId !== strId))
  }

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) setSelectedRows(tableData.map((row: RowData) => String(row.id)))
    else setSelectedRows([])
  }

  const handleDelete = async () => {
    try {
      await Promise.all(selectedRows.map((id) => deleteMutation.mutateAsync(id)))
      setSelectedRows([])
    } catch {
      // list refetch handled by mutation success
    }
  }

  return (
    <Card width="100%" p="0">
      <Grid>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          height="64px"
          flexWrap="wrap"
          gap={4}
          minW={0}
          px="20px"
        >
          <SearchInput onSearch={handleSearch} />

          <Flex gap="8px" flexShrink={0}>
            {selectedRows.length > 0 ? (
              <Button
                onClick={handleDelete}
                variant="error"
                size="sm"
                h="32px"
                px="14px"
                fontSize="13px"
                fontWeight="600"
                borderRadius="8px"
              >
                <Icon fill="none" color="error.100" mr={2}>
                  <Trash04Icon />
                </Icon>
                {t('planning.actions.delete')} ({selectedRows.length})
              </Button>
            ) : (
              <>
                <FilterButton
                  label={t('common.columns.jobProfile')}
                  options={jobProfileFilterOptions}
                  onApply={(selected) => updateFilter('job_profile', selected)}
                />
                <FilterButton
                  label={t('common.fields.country')}
                  options={countryFilterOptions}
                  onApply={(selected) => updateFilter('country', selected)}
                />
                <Button
                  variant="mono"
                  onClick={() => setIsVacancyModalOpen(true)}
                  size="sm"
                  h="32px"
                  px="14px"
                  fontSize="13px"
                  fontWeight="600"
                  borderRadius="8px"
                >
                  <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="white">
                    <PlusIcon />
                  </Icon>
                  {t('recruitment.actions.addVacancyText')}
                </Button>
              </>
            )}
          </Flex>
        </Flex>

        {vacancyTexts.isLoading ? (
          <LoadingTable columns={columns} />
        ) : (
          <Box minHeight="58vh" overflowX="auto" overflowY="visible">
            <MasterTable
              columns={columns}
              data={tableData}
              selectedRows={selectedRows}
              onSelectAll={handleSelectAll}
              onSelectRow={handleSelectRow}
              onRowClick={(row) => navigate(`/user/recruitment/vacancy/${row.id}`)}
              allSelected={tableData.length > 0 && selectedRows.length === tableData.length}
              isIndeterminate={
                tableData.length > 0 &&
                selectedRows.length > 0 &&
                selectedRows.length < tableData.length
              }
              tableType="people"
            />
          </Box>
        )}

        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          maxPage={vacancyTexts?.data?.max_page || 0}
          totalItems={vacancyTexts?.data?.count || 0}
          handlePreviousPage={() => setPage(page - 1)}
          handleNextPage={() => setPage(page + 1)}
          handlePageClick={(p: number) => setPage(p)}
        />
      </Grid>

      <VacancyTextModal
        isOpen={isVacancyModalOpen}
        onClose={() => setIsVacancyModalOpen(false)}
      />
    </Card>
  )
}

export default VacancyTable
